import React, { useEffect, useState } from "react";
import { api } from "../lib/api";
import { handleApiError } from "../lib/handleApiError";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "./ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "./ui/select";
import { toast } from "sonner";
import BlacklistWarning from "./BlacklistWarning";
import RiskBreakdown from "./RiskBreakdown";

const empty = {
  customer_id: "",
  product_name: "",
  imei: "",
  sale_price: "",
  down_payment: "0",
  installment_type: "monthly",
  installment_amount: "",
  duration: "6",
  start_date: new Date().toISOString().slice(0, 10),
  salesman_id: "",
  cost_price: "",
  profit_type: "percent",
  profit_value: "",
};

export default function LoanDialog({ open, onOpenChange, customerId, customers, onSaved, initial }) {
  const [form, setForm] = useState(empty);
  const [custList, setCustList] = useState(customers || []);
  const [salesmen, setSalesmen] = useState([]);
  const [blacklistState, setBlacklistState] = useState({ matches: [], mode: "warn" });
  const [pricingEnabled, setPricingEnabled] = useState(false);

  const selectedCustomer = custList.find((c) => c.id === form.customer_id);

  useEffect(() => {
    if (open) {
      if (initial) {
        setForm({
          customer_id: initial.customer_id || customerId || "",
          product_name: initial.product_name || "",
          imei: initial.imei || "",
          sale_price: String(initial.sale_price ?? ""),
          down_payment: String(initial.down_payment ?? "0"),
          installment_type: initial.installment_type || "monthly",
          installment_amount: String(initial.installment_amount ?? ""),
          duration: String(initial.duration ?? "6"),
          start_date: initial.start_date || new Date().toISOString().slice(0, 10),
          salesman_id: initial.salesman_id || "",
          _id: initial.id,
        });
      } else {
        setForm({ ...empty, customer_id: customerId || "" });
      }
      if (!customers) {
        api.get("/customers").then((r) => setCustList(r.data));
      }
      api.get("/salesmen").then((r) => setSalesmen(r.data || [])).catch(() => {});
      api.get("/auth/shop").then((r) => setPricingEnabled(!!r.data.internal_pricing_enabled)).catch(() => {});
    }
  }, [open, initial, customerId, customers]);

  // Update initial form when edit-mode loan has pricing fields
  useEffect(() => {
    if (open && initial && pricingEnabled) {
      setForm((f) => ({
        ...f,
        cost_price: initial.cost_price != null ? String(initial.cost_price) : "",
        profit_type: initial.profit_type || "percent",
        profit_value: initial.profit_value != null ? String(initial.profit_value) : "",
      }));
    }
  }, [open, initial, pricingEnabled]);

  // Live-compute profit + suggested sale price when cost/profit fields change.
  // Owner can still override the sale_price manually.
  const computedProfit = (() => {
    const cost = Number(form.cost_price || 0);
    const val = Number(form.profit_value || 0);
    if (!cost && form.profit_type === "percent") return null;
    if (!val) return null;
    if (form.profit_type === "percent") return Math.round((cost * val / 100) * 100) / 100;
    return Math.round(val * 100) / 100;
  })();
  const computedSale = form.cost_price && computedProfit != null
    ? Math.round((Number(form.cost_price) + computedProfit) * 100) / 100
    : null;

  const submit = async (e) => {
    e.preventDefault();
    // Blacklist soft-confirm in warn mode
    if (!form._id && blacklistState.matches.length > 0 && blacklistState.mode === "warn") {
      const ok = window.confirm(
        `Customer blacklist mein hai (${blacklistState.matches.length} match). Phir bhi loan banani hai?`
      );
      if (!ok) return;
    }
    const payload = {
      customer_id: form.customer_id,
      product_name: form.product_name,
      imei: form.imei,
      sale_price: Number(form.sale_price),
      down_payment: Number(form.down_payment || 0),
      installment_type: form.installment_type,
      installment_amount: form.installment_type === "custom" ? Number(form.installment_amount) : null,
      duration: Number(form.duration),
      start_date: form.start_date,
      salesman_id: form.salesman_id || null,
    };
    // Internal pricing — only sent when feature is on AND owner entered a cost
    if (pricingEnabled && form.cost_price !== "") {
      payload.cost_price = Number(form.cost_price);
      payload.profit_type = form.profit_type;
      payload.profit_value = form.profit_value !== "" ? Number(form.profit_value) : null;
    }
    try {
      if (form._id) {
        await api.put(`/loans/${form._id}`, payload);
        toast.success("Loan updated");
        onOpenChange(false);
        onSaved?.();
      } else {
        const { data: created } = await api.post("/loans", payload);
        onOpenChange(false);
        // Hand-off to parent — parent shows LoanCreatedDialog (skip OR generate agreement)
        onSaved?.(created);
      }
    } catch (err) {
      handleApiError(err, "Failed to save loan");
    }
  };

  const financed = Math.max(0, Number(form.sale_price || 0) - Number(form.down_payment || 0));
  const estInst =
    form.installment_type === "custom"
      ? Number(form.installment_amount || 0)
      : Number(form.duration) > 0
      ? Math.round(financed / Number(form.duration))
      : 0;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="max-w-xl max-h-[90vh] overflow-y-auto"
        data-testid="loan-dialog"
      >
        <DialogHeader>
          <DialogTitle>{form._id ? "Edit Loan" : "New Loan"}</DialogTitle>
        </DialogHeader>
        {!form._id && selectedCustomer && (
          <BlacklistWarning
            cnic={selectedCustomer.cnic}
            mobile={selectedCustomer.mobile}
            onResult={setBlacklistState}
          />
        )}
        {!form._id && form.customer_id && (
          <div className="rounded-md border border-slate-200 bg-slate-50 p-3" data-testid="loan-risk-preview">
            <div className="text-xs font-bold uppercase tracking-wider text-slate-500 mb-2">Risk Preview</div>
            <RiskBreakdown customerId={form.customer_id} compact />
          </div>
        )}
        <form onSubmit={submit} className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          {!customerId && (
            <div className="space-y-1.5 sm:col-span-2">
              <Label>Customer *</Label>
              <Select value={form.customer_id} onValueChange={(v) => setForm({ ...form, customer_id: v })}>
                <SelectTrigger data-testid="loan-customer-select"><SelectValue placeholder="Select customer" /></SelectTrigger>
                <SelectContent>
                  {custList.map((c) => (
                    <SelectItem key={c.id} value={c.id}>{c.name} · {c.mobile}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}
          <div className="space-y-1.5 sm:col-span-2">
            <Label>Product name *</Label>
            <Input required value={form.product_name} onChange={(e) => setForm({ ...form, product_name: e.target.value })} placeholder="e.g. Infinix Hot 40" data-testid="loan-product-input" />
          </div>
          <div className="space-y-1.5">
            <Label>IMEI / Serial</Label>
            <Input value={form.imei} onChange={(e) => setForm({ ...form, imei: e.target.value })} data-testid="loan-imei-input" />
          </div>
          <div className="space-y-1.5">
            <Label>Start date *</Label>
            <Input type="date" required value={form.start_date} onChange={(e) => setForm({ ...form, start_date: e.target.value })} data-testid="loan-start-input" />
          </div>
          <div className="space-y-1.5">
            <Label>Sale price (PKR) *</Label>
            <Input type="number" required value={form.sale_price} onChange={(e) => setForm({ ...form, sale_price: e.target.value })} data-testid="loan-sale-input" />
            {pricingEnabled && computedSale != null && Number(form.sale_price) !== computedSale && (
              <button type="button" onClick={() => setForm({ ...form, sale_price: String(computedSale) })} className="text-[11px] text-emerald-700 hover:underline" data-testid="loan-apply-suggested-sale">
                Use suggested Rs {computedSale.toLocaleString()} (cost + profit)
              </button>
            )}
          </div>
          <div className="space-y-1.5">
            <Label>Down payment (PKR)</Label>
            <Input type="number" value={form.down_payment} onChange={(e) => setForm({ ...form, down_payment: e.target.value })} data-testid="loan-down-input" />
          </div>
          <div className="space-y-1.5">
            <Label>Installment type *</Label>
            <Select value={form.installment_type} onValueChange={(v) => setForm({ ...form, installment_type: v })}>
              <SelectTrigger data-testid="loan-type-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="weekly">Weekly</SelectItem>
                <SelectItem value="monthly">Monthly</SelectItem>
                <SelectItem value="custom">Custom</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1.5">
            <Label>Number of installments *</Label>
            <Input type="number" required min={1} value={form.duration} onChange={(e) => setForm({ ...form, duration: e.target.value })} data-testid="loan-duration-input" />
          </div>
          {form.installment_type === "custom" && (
            <div className="space-y-1.5 sm:col-span-2">
              <Label>Custom installment amount (PKR) *</Label>
              <Input type="number" required value={form.installment_amount} onChange={(e) => setForm({ ...form, installment_amount: e.target.value })} data-testid="loan-custom-input" />
            </div>
          )}

          <div className="space-y-1.5 sm:col-span-2">
            <Label>Salesman</Label>
            <Select value={form.salesman_id || "__none__"} onValueChange={(v) => setForm({ ...form, salesman_id: v === "__none__" ? "" : v })}>
              <SelectTrigger data-testid="loan-salesman-select"><SelectValue placeholder="Assign salesman…" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="__none__">— Unassigned —</SelectItem>
                {salesmen.map((s) => (
                  <SelectItem key={s.id} value={s.id}>{s.name} · {s.role}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="sm:col-span-2 rounded-md bg-slate-50 border border-slate-200 p-3 text-sm grid grid-cols-2 gap-2">
            <div><span className="text-slate-500">Financed:</span> <span className="tabular font-semibold">Rs {financed.toLocaleString()}</span></div>
            <div><span className="text-slate-500">Per installment:</span> <span className="tabular font-semibold">Rs {estInst.toLocaleString()}</span></div>
          </div>

          {/* Internal Pricing (owner-only, hidden from Agreement PDF) */}
          {pricingEnabled && (
            <div className="sm:col-span-2 rounded-md border border-emerald-200 bg-emerald-50/50 p-3 space-y-3" data-testid="loan-internal-pricing">
              <div className="flex items-center justify-between">
                <div>
                  <div className="text-sm font-display font-bold text-emerald-800">Internal Pricing</div>
                  <div className="text-[11px] text-emerald-700/70">Sirf aap (owner) ko visible — customer ya Agreement PDF par nahi dikhe ga.</div>
                </div>
                <span className="text-[10px] uppercase tracking-wider font-bold text-emerald-700 bg-emerald-100 px-2 py-0.5 rounded-full">Owner Only</span>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                <div className="space-y-1.5">
                  <Label>Buy / Cost price (PKR)</Label>
                  <Input
                    type="number" value={form.cost_price}
                    onChange={(e) => setForm({ ...form, cost_price: e.target.value })}
                    placeholder="20000"
                    data-testid="loan-cost-input"
                  />
                </div>
                <div className="space-y-1.5">
                  <Label>Profit type</Label>
                  <Select value={form.profit_type} onValueChange={(v) => setForm({ ...form, profit_type: v })}>
                    <SelectTrigger data-testid="loan-profit-type-select"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="percent">Percentage (%)</SelectItem>
                      <SelectItem value="fixed">Fixed amount (PKR)</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-1.5">
                  <Label>Profit value</Label>
                  <Input
                    type="number" value={form.profit_value}
                    onChange={(e) => setForm({ ...form, profit_value: e.target.value })}
                    placeholder={form.profit_type === "percent" ? "25" : "5000"}
                    data-testid="loan-profit-value-input"
                  />
                </div>
              </div>
              {computedProfit != null && (
                <div className="text-xs grid grid-cols-2 sm:grid-cols-3 gap-2 pt-1 border-t border-emerald-200" data-testid="loan-profit-summary">
                  <div><span className="text-slate-500">Profit:</span> <span className="tabular font-bold text-emerald-700">Rs {computedProfit.toLocaleString()}</span></div>
                  {computedSale != null && (
                    <div><span className="text-slate-500">Suggested sale:</span> <span className="tabular font-bold">Rs {computedSale.toLocaleString()}</span></div>
                  )}
                  {form.cost_price && (
                    <div><span className="text-slate-500">Margin:</span> <span className="tabular font-bold text-emerald-700">{Math.round((computedProfit / Number(form.cost_price)) * 100)}%</span></div>
                  )}
                </div>
              )}
            </div>
          )}

          <DialogFooter className="sm:col-span-2">
            <Button
              type="submit"
              className="bg-emerald-600 hover:bg-emerald-700"
              disabled={!form._id && blacklistState.matches.length > 0 && blacklistState.mode === "block"}
              data-testid="loan-save-btn"
            >
              {(!form._id && blacklistState.matches.length > 0 && blacklistState.mode === "block")
                ? "Blocked — Blacklisted"
                : `${form._id ? "Update" : "Create"} Loan`}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
